// frontend/app/components/admin/adminTable.js
//
// The table and list machinery the admin screens share: column geometry, the
// page size, and the two functions that make a paged list readable.
//
// Lifted out of InternalAdminPanel.js rather than copied into the lesson
// library. They were already pure and dependency-free, and that file is past
// 7,000 lines — a second copy would have made both problems worse.
//
// Only what has no dependencies moved. `colStyle` and `renderTableCount` stay
// in the panel: both read its local StyleSheet, and 150 call sites is not a
// price worth paying to pass a style through.

/** Rows per page across every admin list. */
export const PAGE_SIZE = 10;

export const USER_COLS = {
  email: { minWidth: 220, flex: 3.2 },
  role: { minWidth: 110, flex: 1.4 },
  school: { minWidth: 180, flex: 2.8 },
  scienceCenter: { minWidth: 140, flex: 1.6 },
  grade: { minWidth: 80, flex: 1.0 },
  accessEnd: { minWidth: 130, flex: 1.6 },
  trial: { minWidth: 110, flex: 1.2 },
  contentDb: { minWidth: 120, flex: 1.4 },
  engagement: { minWidth: 260, flex: 3.2 },
  action: { minWidth: 150, flex: 1.8 },
};
export const DR_COLS = {
  targetType: { minWidth: 120, flex: 1.4 },
  target: { minWidth: 190, flex: 2.6 },
  requester: { minWidth: 180, flex: 2.2 },
  requestType: { minWidth: 120, flex: 1.4 },
  from: { minWidth: 110, flex: 1.1 },
  to: { minWidth: 110, flex: 1.1 },
  status: { minWidth: 110, flex: 1.2 },
  requested: { minWidth: 110, flex: 1.2 },
  action: { minWidth: 140, flex: 1.6 },
};
export const SCHOOL_COLS = {
  name: { minWidth: 220, flex: 3.0 },
  code: { minWidth: 130, flex: 1.8 },
  partner: { minWidth: 180, flex: 2.0 },
  students: { minWidth: 90, flex: 1.2 },
  engagement: { minWidth: 260, flex: 3.0 },
  action: { minWidth: 120, flex: 1.6 },
};
export const CLASS_COLS = {
  name: { minWidth: 200, flex: 2.8 },
  code: { minWidth: 120, flex: 1.4 },
  school: { minWidth: 200, flex: 2.8 },
  active: { minWidth: 100, flex: 1.2 },
  action: { minWidth: 110, flex: 1.4 },
};
export const PARTNER_COLS = {
  name: { minWidth: 220, flex: 2.8 },
  code: { minWidth: 130, flex: 1.6 },
  contactEmail: { minWidth: 230, flex: 3.0 },
  schools: { minWidth: 90, flex: 1.0 },
};
export const BILLING_COLS = {
  email: { minWidth: 220, flex: 2.8 },
  partner: { minWidth: 200, flex: 2.4 },
  school: { minWidth: 200, flex: 2.4 },
  billingCycle: { minWidth: 120, flex: 1.4 },
  pending: { minWidth: 120, flex: 1.4 },
  trial: { minWidth: 110, flex: 1.2 },
  accessEnd: { minWidth: 130, flex: 1.4 },
  nextBilling: { minWidth: 130, flex: 1.4 },
  remaining: { minWidth: 140, flex: 1.6 },
};
export const BILLING_SCHOOL_COLS = {
  partner: { minWidth: 200, flex: 2.4 },
  school: { minWidth: 200, flex: 2.4 },
  users: { minWidth: 100, flex: 1.2 },
  billingCycle: { minWidth: 120, flex: 1.4 },
  pending: { minWidth: 120, flex: 1.4 },
  lastPayment: { minWidth: 130, flex: 1.4 },
  nextBilling: { minWidth: 130, flex: 1.4 },
  remaining: { minWidth: 140, flex: 1.6 },
  trial: { minWidth: 110, flex: 1.2 },
  action: { minWidth: 140, flex: 1.6 },
};
export const PLAN_COLS = {
  code: { minWidth: 120, flex: 1.4 },
  name: { minWidth: 220, flex: 2.6 },
  duration: { minWidth: 120, flex: 1.2 },
  price: { minWidth: 120, flex: 1.2 },
  currency: { minWidth: 90, flex: 0.9 },
  region: { minWidth: 120, flex: 1.2 },
  category: { minWidth: 120, flex: 1.2 },
  active: { minWidth: 90, flex: 0.9 },
  action: { minWidth: 120, flex: 1.2 },
};
export const DISCOUNT_COLS = {
  code: { minWidth: 140, flex: 1.6 },
  type: { minWidth: 100, flex: 1.1 },
  amount: { minWidth: 120, flex: 1.2 },
  percent: { minWidth: 100, flex: 1.0 },
  currency: { minWidth: 90, flex: 0.9 },
  expires: { minWidth: 130, flex: 1.2 },
  active: { minWidth: 90, flex: 0.9 },
  action: { minWidth: 120, flex: 1.2 },
};
export const CAMP_LINK_COLS = {
  studentName: { minWidth: 170, flex: 2.0 },
  parentEmail: { minWidth: 240, flex: 2.8 },
  amount: { minWidth: 120, flex: 1.2 },
  paymentStatus: { minWidth: 120, flex: 1.2 },
  linkSent: { minWidth: 120, flex: 1.2 },
  createdAt: { minWidth: 140, flex: 1.4 },
  paidAt: { minWidth: 140, flex: 1.4 },
  parentName: { minWidth: 180, flex: 1.8 },
  shippingAddress: { minWidth: 320, flex: 3 },
  parentContactNumber: { minWidth: 170, flex: 1.6 },
  actions: { minWidth: 130, flex: 1.2 },
};

export const sumMinWidth = (cols) =>
  Object.values(cols).reduce((sum, v) => sum + (v?.minWidth || 0), 0);
export const USERS_TABLE_MIN_WIDTH = sumMinWidth(USER_COLS);
export const DR_TABLE_MIN_WIDTH = sumMinWidth(DR_COLS);
export const SCHOOLS_TABLE_MIN_WIDTH = sumMinWidth(SCHOOL_COLS);
export const CLASSES_TABLE_MIN_WIDTH = sumMinWidth(CLASS_COLS);
export const PARTNERS_TABLE_MIN_WIDTH = sumMinWidth(PARTNER_COLS);
export const BILLING_TABLE_MIN_WIDTH = sumMinWidth(BILLING_COLS);
export const BILLING_SCHOOL_TABLE_MIN_WIDTH = sumMinWidth(BILLING_SCHOOL_COLS);
export const PLAN_TABLE_MIN_WIDTH = sumMinWidth(PLAN_COLS);
export const DISCOUNT_TABLE_MIN_WIDTH = sumMinWidth(DISCOUNT_COLS);
export const CAMP_LINK_TABLE_MIN_WIDTH = sumMinWidth(CAMP_LINK_COLS);

/**
 * A list response, from a route that returns a bare array and from one that
 * returns `{ items, total }`. `total` is null when the server did not say —
 * which is not the same as zero, and must not be shown as a count.
 */
export const normalizeListResponse = (payload) => {
  if (Array.isArray(payload)) {
    return { items: payload, total: null };
  }
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const total =
    typeof payload?.total === "number" && Number.isFinite(payload.total)
      ? payload.total
      : null;
  return { items, total };
};

/** "11–20 of 34 users", or "0 of 0 users" when there is nothing to show. */
export const formatCountLabel = (offsetValue, pageCount, total, label) => {
  const safePage = Number.isFinite(pageCount) ? pageCount : 0;
  const safeTotal = Number.isFinite(total) ? total : safePage;
  if (safeTotal === 0 || safePage === 0) {
    return `0 of ${safeTotal} ${label}`;
  }
  const start = Math.max(1, (Number.isFinite(offsetValue) ? offsetValue : 0) + 1);
  const end = Math.min(start + safePage - 1, safeTotal);
  return `${start}–${end} of ${safeTotal} ${label}`;
};
